import * as React from 'react';
import classnames from 'classnames';
import { Switch, renderIcon } from '@ixicare/ui';
import { Field, FieldValue, FieldValues, same, Section } from './schema';
import FieldRenderer from './fields/FieldRenderer';
import styles from './styles.module.css';
import { t } from '~i18n';
import { GatingContext } from './utils/gating';
import { DifferingKeyDetail } from './fields/FieldWrapper';
import { ControlOverrides } from './fields/controls';
import SharedSectionCard from '~components/SectionCard';

interface Props {
  sectionId: string;
  section: Section;
  values: FieldValues;
  initialValues: FieldValues;
  errors: Record<string, string>;
  gating: GatingContext;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onChange: (id: string, value: FieldValue) => void;
  differingDetail?: Map<string, DifferingKeyDetail[]>;
  inheritedDetail?: Map<string, DifferingKeyDetail[]>;
  // Bulk mode: device-level fields (e.g. phone_number) are per-device and hidden.
  hideDeviceModelLevel?: boolean;
  /**
   * Fields the form holds at their current value, naming them rather than deriving it.
   *
   * `enabled_if` covers a field disabled by one other field's value; this is for a rule the
   * definition cannot state -- an allow list forces outdoor cover on, which depends on two fields
   * and is a correction the page makes, not something the service describes. Without it the
   * control looks clickable and silently snaps back, which reads as a broken checkbox.
   */
  disabledFields?: string[];
  readOnly?: boolean;
  dimInherited?: boolean;
  onToggleStored?: (field: Field) => void;
  // The device type this form is being filled in for; an option only some types can act on is not
  // offered on the rest.
  deviceType?: string;
  /** Controls the definition cannot describe; see `fields/controls.ts`. */
  controls?: ControlOverrides;
  /** Off for a form with no layer beneath it; see `fields/FieldWrapper`. Default on. */
  showFlags?: boolean;
  /**
   * The sysadmin affordances: a field count, a changed count, and the collapse. Off for a
   * carer-facing form, where the section is short, always open, and the details are not theirs to
   * reason about.
   */
  showChrome?: boolean;
  /** Rendered beside a named field, in the same row -- for what the definition cannot place. */
  beside?: Record<string, React.ReactNode>;
}

// Map (section.cols, field.span) onto the 12-column @ixicare/ui grid.
// section.cols=2 → each field is col-6; span=2 → col-12. cols=4 → col-3,
// span=2 → col-6, etc. Capped at 12.
const colSpan = (sectionCols: number | undefined, fieldSpan?: number) => {
  const cols = sectionCols ?? 2;
  const base = 12 / cols;
  return Math.min(12, base * (fieldSpan ?? 1));
};

const SectionCard: React.FC<Props> = ({
  sectionId,
  section,
  values,
  initialValues,
  errors,
  gating,
  collapsed,
  onToggleCollapsed,
  onChange,
  differingDetail,
  inheritedDetail,
  hideDeviceModelLevel,
  readOnly,
  disabledFields,
  dimInherited,
  onToggleStored,
  deviceType,
  controls,
  showFlags,
  showChrome = true,
  beside,
}) => {
  const [showAdvanced, setShowAdvanced] = React.useState<boolean>(false);

  // Advanced fields are hidden until the section's advanced toggle is on.
  // Device-level fields are hidden in bulk mode (they're per-device).
  const visibleFields = section.fields.filter(
    (f) => (showAdvanced || !f.advanced) && !(hideDeviceModelLevel && f.device_model_level)
  );

  const changedCount = React.useMemo(() => {
    const staged = new Set<string>();
    for (const f of section.fields) {
      const source = inheritedDetail?.get(f.id)?.[0]?.source;
      if (source === 'own-cleared' || source === 'own-will-set') staged.add(f.id);
    }
    return section.fields.filter(
      (f) => staged.has(f.id) || (!same(values[f.id], initialValues[f.id]) && initialValues[f.id] !== undefined)
    ).length;
  }, [section.fields, values, initialValues, inheritedDetail]);

  const differingCount = differingDetail ? visibleFields.filter((f) => differingDetail.has(f.id)).length : 0;

  const revealedFieldIds = React.useMemo(() => {
    const ids = new Set<string>();
    for (const field of section.fields) {
      field.reveals?.forEach((id) => ids.add(id));
      for (const option of field.options ?? []) {
        if (typeof option !== 'string') option.reveals?.forEach((id) => ids.add(id));
      }
    }
    return ids;
  }, [section.fields]);
  // Still every visible field, revealed ones included: the badge counts what the section holds,
  // not how it is laid out.
  const topLevelFields = visibleFields.filter((f) => !revealedFieldIds.has(f.id));

  const fieldById = React.useMemo(() => new Map(section.fields.map((field) => [field.id, field])), [section.fields]);

  // Nested, so no grid cell and no revealing of its own: one level is what the controls draw, and
  // a field revealed by a field revealed by a field would have nowhere legible to go.
  const renderRevealed = (ids: string[]): React.ReactNode =>
    ids.length === 0
      ? null
      : ids.map((id) => {
          const field = fieldById.get(id);
          if (!field) return null;
          return (
            <FieldRenderer
              key={field.id}
              field={field}
              values={values}
              initialValue={initialValues[field.id]}
              initialValues={initialValues}
              error={errors[field.id]}
              onChange={onChange}
              gating={gating}
              readOnly={readOnly}
              disabledFields={disabledFields}
              dimInherited={dimInherited}
              onToggleStored={onToggleStored}
              differingDetail={differingDetail}
              inheritedDetail={inheritedDetail}
              deviceType={deviceType}
              controls={controls}
              showFlags={showFlags}
            />
          );
        });

  const renderField = (field: Field) => (
    <div key={field.id} className={classnames(styles.fieldCell, `col-${colSpan(section.cols, field.span)}`)}>
      <FieldRenderer
        field={field}
        values={values}
        initialValue={initialValues[field.id]}
        initialValues={initialValues}
        error={errors[field.id]}
        onChange={onChange}
        gating={gating}
        readOnly={readOnly}
        disabledFields={disabledFields}
        dimInherited={dimInherited}
        onToggleStored={onToggleStored}
        differingDetail={differingDetail}
        inheritedDetail={inheritedDetail}
        deviceType={deviceType}
        controls={controls}
        showFlags={showFlags}
        renderRevealed={renderRevealed}
      />
      {beside?.[field.id]}
    </div>
  );

  let body: React.ReactNode;
  if (section.sub_sections && section.sub_sections.length) {
    const groups = new Map<string, Field[]>();
    section.sub_sections.forEach((s) => groups.set(s.id, []));
    const ungrouped: Field[] = [];
    topLevelFields.forEach((f) => {
      if (f.sub_section && groups.has(f.sub_section)) {
        groups.get(f.sub_section)!.push(f);
      } else {
        ungrouped.push(f);
      }
    });
    body = (
      <>
        {ungrouped.length > 0 && (
          <div className={styles.subSection}>
            <div className="row">{ungrouped.map(renderField)}</div>
          </div>
        )}
        {section.sub_sections.map((s) => {
          const fields = groups.get(s.id) || [];
          if (fields.length === 0) return null;
          return (
            <div className={styles.subSection} key={s.id}>
              <div className={styles.subHead}>{t(s.title_key, s.id)}</div>
              <div className="row">{fields.map(renderField)}</div>
            </div>
          );
        })}
      </>
    );
  } else {
    body = <div className="row">{topLevelFields.map(renderField)}</div>;
  }

  return (
    <SharedSectionCard
      data-sec={sectionId}
      title={t(section.title_key, sectionId)}
      collapsed={showChrome ? collapsed : false}
      onToggleCollapsed={showChrome ? onToggleCollapsed : undefined}
      bodyClassName={styles.sectionBody}
      icon={
        section.icon && (
          <span
            className={styles.sectionIcon}
            style={section.icon_color ? { color: section.icon_color } : undefined}
            aria-hidden
          >
            {renderIcon(section.icon, undefined)}
          </span>
        )
      }
      badges={
        showChrome && (
          <>
            <span className={styles.sectionCount}>
              {t('device-config.common.fieldCount', { count: visibleFields.length })}
            </span>
            {changedCount > 0 && (
              <span className={styles.changedDot}>
                ● {changedCount} {t('device-config.common.changed')}
              </span>
            )}
            {differingCount > 0 && (
              <span className={styles.differingDot}>
                ● {differingCount} {t('device-config.bulk.differing')}
              </span>
            )}
          </>
        )
      }
      headerRight={
        section.advanced_toggle ? (
          <div className={styles.advancedTogglePill}>
            <Switch label={t('device-config.common.show-advanced')} checked={showAdvanced} onChange={setShowAdvanced} />
          </div>
        ) : undefined
      }
    >
      {body}
    </SharedSectionCard>
  );
};

export default SectionCard;
